import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConditionActions } from './condition.actions';
import {
  selectActiveConditions,
  selectCondition,
  selectConditionError,
  selectConditions,
  selectConditionsLoaded,
  selectConditionsLoading,
} from './condition.selectors';
import { ICondition, ICreateConditionDTO } from './interfaces/condition.interface';

/**
 * Façade over the condition store. Components read the signals from here; the API service is only
 * ever touched by the effects.
 */
@Injectable({
  providedIn: 'root',
})
export class ConditionService {
  private readonly store = inject(Store);

  readonly conditions = this.store.selectSignal(selectConditions);
  readonly activeConditions = this.store.selectSignal(selectActiveConditions);
  readonly loaded = this.store.selectSignal(selectConditionsLoaded);
  readonly loading = this.store.selectSignal(selectConditionsLoading);
  readonly error = this.store.selectSignal(selectConditionError);

  readonly conditions$ = this.store.select(selectConditions);
  readonly activeConditions$ = this.store.select(selectActiveConditions);

  condition(id: string) {
    return this.store.selectSignal(selectCondition(id));
  }

  load(): void {
    this.store.dispatch(ConditionActions.loadConditions());
  }

  /**
   * Loads the catalogue once. Dashboards mount many charts at the same time, so every chart calling
   * this is cheaper than each of them fetching.
   */
  ensureLoaded(): void {
    if (this.loaded() || this.loading()) return;
    this.load();
  }

  create(dto: ICreateConditionDTO): void {
    this.store.dispatch(ConditionActions.createCondition({ dto }));
  }

  update(id: string, dto: Partial<ICreateConditionDTO>): void {
    this.store.dispatch(ConditionActions.updateCondition({ id, dto }));
  }

  delete(id: string): void {
    this.store.dispatch(ConditionActions.deleteCondition({ id }));
  }

  byId(conditions: ICondition[]): Map<string, ICondition> {
    return new Map(conditions.map((c) => [c.id, c]));
  }
}
